import { useState } from "react";
import { Clock, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { LegalPage } from "@/components/layout/LegalPage";
import { useCompanySettings } from "@/context/SettingsContext";
import { waLink } from "@/lib/constants";

const InfoRow = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-start gap-3" data-testid={testId}>
    <span className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
      <Icon className="h-4 w-4" />
    </span>
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm font-medium mt-0.5">{value || "—"}</p>
    </div>
  </div>
);

export default function ContactUs() {
  const { settings } = useCompanySettings();
  const [form, setForm] = useState({ name: "", mobile: "", message: "" });

  const sendViaWhatsApp = () => {
    if (!form.name.trim() || !form.message.trim()) {
      return toast.error("Please enter your name and message");
    }
    const text = `Hi, I'm ${form.name.trim()}${form.mobile ? ` (${form.mobile})` : ""}. ${form.message.trim()}`;
    window.open(`${waLink(settings.support_whatsapp_number)}?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <LegalPage title="Contact Us" subtitle="We're here to help — reach us anytime during office hours." testId="contact-page">
      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <div className="space-y-5">
          <Card className="rounded-xl border-border shadow-none">
            <CardContent className="p-5 space-y-4">
              <InfoRow icon={MapPin} label="Office Address" value={settings.office_address} testId="contact-address" />
              <InfoRow icon={Phone} label="Contact Number" value={settings.contact_number} testId="contact-phone" />
              <InfoRow icon={Mail} label="Support Email" value={settings.support_email} testId="contact-email" />
              <InfoRow icon={Clock} label="Office Hours" value={settings.office_hours} testId="contact-hours" />
            </CardContent>
          </Card>

          {/* Google Maps placeholder */}
          <div className="rounded-xl border border-border bg-muted/50 h-52 flex flex-col items-center justify-center text-muted-foreground" data-testid="contact-map-placeholder">
            <MapPin className="h-8 w-8 mb-2 text-primary/50" />
            <p className="text-sm font-medium">Google Maps</p>
            <p className="text-xs">Map will be embedded here</p>
          </div>
        </div>

        <Card className="rounded-xl border-border shadow-none">
          <CardContent className="p-5 space-y-4">
            <div>
              <p className="font-heading font-semibold">Send us a message</p>
              <p className="text-xs text-muted-foreground mt-1">Your message opens directly in WhatsApp — our fastest support channel.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Your name</Label>
              <Input data-testid="contact-form-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Mobile number (optional)</Label>
              <Input data-testid="contact-form-mobile" inputMode="numeric" maxLength={10} value={form.mobile} onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value.replace(/\D/g, "") }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Message</Label>
              <Textarea data-testid="contact-form-message" rows={4} placeholder="How can we help you?" value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} />
            </div>
            <Button onClick={sendViaWhatsApp} className="w-full rounded-lg bg-emerald-600 hover:bg-emerald-700" data-testid="contact-form-submit">
              <MessageCircle className="h-4 w-4 mr-2" /> Send via WhatsApp
            </Button>
          </CardContent>
        </Card>
      </div>
    </LegalPage>
  );
}
